import type {
  RepositoryIntelligenceCitation,
  RepositoryIntelligenceQuestion,
} from '@ashniva/types';
import { Alert, Badge } from '@ashniva/ui';

import { answerSegments } from '../answer-text';
import { shortCommit } from '../freshness';
import { CONFIDENCE, linesLabel, questionFailure } from '../ri-text';
import { useWorkspace } from '../workspace-context';

/** Desk's own reminder, shown with every answer whatever else is said. */
export const DESK_DISCLAIMER =
  'Answers are put together from the indexed code at the commit shown and can be incomplete or wrong. Check the cited lines before relying on them.';

const citationLabel = (citation: RepositoryIntelligenceCitation) =>
  `Citation ${citation.ref}: ${citation.path}, ${linesLabel(citation.startLine, citation.endLine)}${
    citation.verified ? '' : ' (not verified)'
  }`;

/**
 * An answer (MASTER-SPEC §14.4, P14-06): plain text in which each `[E3]` that names a citation is a button opening
 * the cited lines; the confidence as a word, not only a colour; the commit it is about and the disclaimers, always.
 * Nothing the answer says is interpreted: no Markdown, no HTML, no links.
 */
export function AnswerView({ result }: { result: RepositoryIntelligenceQuestion }) {
  const { openCode } = useWorkspace();
  const { question, answer } = result;
  const confidence = answer?.confidence ? CONFIDENCE[answer.confidence] : null;
  const insufficient = question.status === 'insufficient_context' || answer?.insufficientContext;
  const open = (citation: RepositoryIntelligenceCitation) =>
    openCode({ path: citation.path, start: citation.startLine, end: citation.endLine });

  return (
    <article className="ri-stack" aria-label="Answer">
      <div className="ri-row">
        {confidence ? <Badge tone={confidence.tone}>Confidence: {confidence.label}</Badge> : null}
        <span className="ri-muted">
          {question.mode === 'evidence' ? 'Evidence mode' : 'Generative mode'} · at commit{' '}
          <span className="ri-mono">{shortCommit(question.commit)}</span>
          {question.cached ? ' · answered before' : ''}
        </span>
      </div>
      {insufficient ? (
        <Alert tone="info" title="Not enough evidence">
          Repository Intelligence could not find enough in the code to answer this. Narrow the
          question to a file, a directory or a symbol, or ask it another way.
        </Alert>
      ) : null}
      {!answer ? (
        <Alert tone="warning" title="No answer">
          {questionFailure(question.errorCode) ?? 'This question could not be answered.'}
        </Alert>
      ) : (
        <>
          <p className="ri-answer__text">
            {answerSegments(answer.text, answer.citations).map((segment, index) =>
              segment.kind === 'text' ? (
                <span key={index}>{segment.text}</span>
              ) : (
                <button
                  key={index}
                  type="button"
                  className="ri-cite"
                  aria-label={citationLabel(segment.citation)}
                  onClick={() => open(segment.citation)}
                >
                  {segment.ref}
                </button>
              ),
            )}
          </p>
          {answer.unsupportedClaims > 0 ? (
            <p className="ri-muted">
              {answer.unsupportedClaims === 1
                ? 'One statement could not be tied to the code and was left out.'
                : `${answer.unsupportedClaims} statements could not be tied to the code and were left out.`}
            </p>
          ) : null}
          {answer.notices.length > 0 ? (
            <ul className="ri-list" aria-label="Notes">
              {answer.notices.map((notice, index) => (
                <li key={index} className="ri-muted">
                  {notice}
                </li>
              ))}
            </ul>
          ) : null}
          {answer.citations.length > 0 ? (
            <section aria-label="Sources">
              <h4>Sources</h4>
              <ul className="ri-list">
                {answer.citations.map((citation) => (
                  <li key={citation.ref}>
                    <button type="button" className="ri-file-link" onClick={() => open(citation)}>
                      {citation.ref}: {citation.path},{' '}
                      {linesLabel(citation.startLine, citation.endLine)}
                    </button>
                    {citation.verified ? null : <span className="ri-muted"> · not verified</span>}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {answer.disclaimer ? <p className="ri-disclaimer">{answer.disclaimer}</p> : null}
        </>
      )}
      <p className="ri-disclaimer">{DESK_DISCLAIMER}</p>
    </article>
  );
}
